import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Users, MapPin, Target, Save, AlertTriangle, CheckCircle2, Eye, Shield } from 'lucide-react';
import Images from '../../utils/Images';

const ChannelFormDrawer = ({
  isOpen,
  channelData,
  setChannelData,
  channelType,
  channelLabel,
  editingIndex,
  onClose,
  onSave,
  errors
}) => {
  const channelPurposes = [
    'Electronics & Gadgets',
    'Fashion & Style',
    'Education',
    'Lifestyle',
    'Hospitality',
    'Technology',
    'Food & Beverages',
    'Health & Fitness',
    'Entertainment',
    'Travel & Tourism',
    'Sports',
    'Beauty & Cosmetics',
    'Home & Living',
    'Business & Finance',
    'General/Mixed Content'
  ];

  const audienceLocations = [
    'Ghana',
    'Nigeria',
    'Kenya',
    'South Africa',
    'United States',
    'United Kingdom',
    'Canada',
    'Australia',
    'Multiple Countries',
    'Campus/University',
    'Local Community'
  ];

  const getChannelLabel = (type) => {
    const labels = {
      whatsapp: 'WhatsApp Number/Link',
      tiktok: 'TikTok Handle',
      instagram: 'Instagram Handle',
      facebook: 'Facebook Page',
      telegram: 'Telegram Channel/Group',
      youtube: 'YouTube Channel',
      website: 'Website URL',
      podcast: 'Podcast Name/RSS Feed',
      mobile_app: 'App Name/Store Link',
      newsletter: 'Newsletter Name/Link',
      offline: 'Community Name'
    };
    return labels[type] || 'Handle/Link';
  };

  const getChannelPlaceholder = (type) => {
    const placeholders = {
      whatsapp: '0249158582 or 233249158582 or https://wa.me/...',
      tiktok: '@username or https://tiktok.com/@username',
      instagram: '@username or https://instagram.com/username',
      facebook: 'Page name or https://facebook.com/pagename',
      telegram: '@channel or https://t.me/channel',
      youtube: 'https://youtube.com/c/channelname',
      website: 'https://yourblog.com',
      podcast: 'Podcast name or RSS feed URL',
      mobile_app: 'App name or store link (e.g., https://play.google.com/store/apps/details?id=...)',
      newsletter: 'Newsletter name or signup page URL (e.g., https://yournewsletter.com/subscribe)',
      offline: 'e.g., KNUST Campus Community, Accra Mall Team'
    };
    return placeholders[type] || 'Enter handle or link';
  };

  const getChannelDescription = (type) => {
    const descriptions = {
      whatsapp: 'Add your WhatsApp number or link to your Status, Group, or Community',
      tiktok: 'Enter your TikTok username or profile link',
      instagram: 'Enter your Instagram username or profile link',
      facebook: 'Enter your Facebook page name or link',
      telegram: 'Enter your Telegram channel or group link',
      youtube: 'Enter your YouTube channel link',
      website: 'Enter your blog or website URL',
      podcast: 'Enter your podcast name or RSS feed URL',
      mobile_app: 'Enter your mobile app name or direct store link',
      newsletter: 'Enter your newsletter name or subscription page link',
      offline: 'Describe your offline community or campus presence'
    };
    return descriptions[type] || 'Enter your channel details';
  };

  const [handleValidation, setHandleValidation] = useState({ isValid: false, message: '' });

  // Validate handle format in real-time
  useEffect(() => {
    if (channelType === 'mobile_app') {
      setHandleValidation({ isValid: false, message: '' });
      return;
    }

    const handle = channelData.handle || channelData.link || '';
    
    if (!handle.trim()) {
      setHandleValidation({ isValid: false, message: '' });
      return;
    }

    // Check if handle looks valid based on channel type
    let isValid = false;
    let message = '';

    switch (channelType) {
      case 'instagram':
        isValid = /^(@[\w.]+|https?:\/\/(www\.)?instagram\.com\/[\w.]+)/i.test(handle);
        message = isValid 
          ? '✓ Handle format looks correct' 
          : '⚠️ Use @username or full Instagram URL (e.g., @yourhandle or https://instagram.com/yourhandle)';
        break;
      case 'tiktok':
        isValid = /^(@[\w.]+|https?:\/\/(www\.)?tiktok\.com\/@[\w.]+)/i.test(handle);
        message = isValid 
          ? '✓ Handle format looks correct' 
          : '⚠️ Use @username or full TikTok URL (e.g., @yourhandle or https://tiktok.com/@yourhandle)';
        break;
      case 'facebook':
        isValid = /^(https?:\/\/(www\.)?(facebook|fb)\.com\/[\w.-]+|[\w\s.-]+)/i.test(handle);
        message = isValid 
          ? '✓ Page name or link looks correct' 
          : '⚠️ Use page name or full Facebook URL (e.g., YourPageName or https://facebook.com/yourpage)';
        break;
      case 'youtube':
        isValid = /^(https?:\/\/(www\.)?(youtube\.com\/(c\/|channel\/|user\/)?|youtu\.be\/)[\w-]+)/i.test(handle);
        message = isValid 
          ? '✓ Channel link looks correct' 
          : '⚠️ Use full YouTube channel URL (e.g., https://youtube.com/c/yourchannel)';
        break;
      case 'telegram':
        isValid = /^(https?:\/\/(t\.me|telegram\.me)\/[\w]+|@[\w]+)/i.test(handle);
        message = isValid 
          ? '✓ Channel/Group link looks correct' 
          : '⚠️ Use @channel or full Telegram URL (e.g., @yourchannel or https://t.me/yourchannel)';
        break;
      case 'whatsapp':
        isValid = /^(\+?\d{10,15}|https?:\/\/(wa\.me|chat\.whatsapp\.com)\/[\w]+)/i.test(handle);
        message = isValid 
          ? '✓ Phone number or link looks correct' 
          : '⚠️ Use phone number (0249158582) or WhatsApp link (https://wa.me/...)';
        break;
      case 'website':
        isValid = /^https?:\/\/.+/i.test(handle);
        message = isValid 
          ? '✓ Website URL looks correct' 
          : '⚠️ Use full website URL starting with http:// or https://';
        break;
      case 'podcast':
        isValid = handle.length > 3;
        message = isValid 
          ? '✓ Podcast name or RSS feed looks valid' 
          : '⚠️ Enter podcast name or RSS feed URL';
        break;
      case 'newsletter':
        isValid = handle.length > 3;
        message = isValid 
          ? '✓ Newsletter name or link looks valid' 
          : '⚠️ Enter newsletter name or subscription page URL';
        break;
      case 'offline':
        isValid = handle.length > 3;
        message = isValid 
          ? '✓ Community name looks valid' 
          : '⚠️ Enter your community or campus name';
        break;
      default:
        isValid = handle.length > 0;
    }

    setHandleValidation({ isValid, message });
  }, [channelData.handle, channelData.link, channelType]);

  const handleInputChange = (field, value) => {
    setChannelData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="overflow-y-auto fixed top-0 right-0 z-50 w-full h-full bg-white shadow-2xl sm:w-[500px]"
    >
      {/* Header */}
      <div className="flex sticky top-0 z-10 justify-between items-center px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 border-b border-red-700">
        <div>
          <h3 className="text-lg font-bold text-white">
            {editingIndex !== null ? 'Edit' : 'Add'} {channelLabel}
          </h3>
          <p className="text-xs text-red-100 mt-1">
            {getChannelDescription(channelType)}
          </p>
        </div>
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.1, rotate: 90 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 text-white rounded-full transition-colors hover:bg-red-700"
        >
          <X className="w-5 h-5" />
        </motion.button>
      </div>

      {/* Form Content */}
      <div className="p-6 space-y-6">
        {/* Channel Handle/Link - Hidden for mobile_app (uses stores instead) */}
        {channelType !== 'mobile_app' && (
          <div>
            {/* Important Verification Notice */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 mb-3 bg-amber-50 rounded-lg border border-amber-200"
            >
              <div className="flex items-start space-x-2">
                <Shield className="flex-shrink-0 w-4 h-4 mt-0.5 text-amber-600" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-amber-900 mb-1">
                    🔍 Verification Required
                  </p>
                  <p className="text-xs text-amber-800">
                    Our team will <strong>visit and verify</strong> this {channelType === 'whatsapp' ? 'phone number' : 'page'} during review. 
                    Enter the <strong>exact handle or URL</strong> as it appears on the platform.
                  </p>
                </div>
              </div>
            </motion.div>

            <label className="flex items-center mb-2 space-x-1 text-sm font-medium text-gray-700">
              <ExternalLink className="w-4 h-4" />
              <span>{getChannelLabel(channelType)}</span>
              <span className="text-red-600">*</span>
            </label>
            
            <div className="relative">
              <input
                type="text"
                value={channelData.handle || channelData.link || ''}
                onChange={(e) => handleInputChange('handle', e.target.value)}
                className={`px-4 py-3 pr-10 w-full text-sm placeholder-gray-500 text-gray-900 bg-white rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors?.handle 
                    ? 'border-red-500' 
                    : handleValidation.isValid && handleValidation.message
                      ? 'border-green-500'
                      : 'border-gray-300'
                }`}
                placeholder={getChannelPlaceholder(channelType)}
              />
              {/* Validation Icon */}
              <AnimatePresence>
                {handleValidation.isValid && (channelData.handle || channelData.link) && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Real-time Validation Message */}
            <AnimatePresence>
              {handleValidation.message && (channelData.handle || channelData.link) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className={`mt-2 flex items-start space-x-2 text-xs ${
                    handleValidation.isValid ? 'text-green-700' : 'text-amber-700'
                  }`}
                >
                  {handleValidation.isValid ? (
                    <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5" />
                  ) : (
                    <AlertTriangle className="flex-shrink-0 w-4 h-4 mt-0.5" />
                  )}
                  <span>{handleValidation.message}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {errors?.handle && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-xs text-red-500 flex items-center space-x-1"
              >
                <AlertTriangle className="w-3 h-3" />
                <span>{errors.handle}</span>
              </motion.p>
            )}

            {/* Format Examples */}
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="mb-2 text-xs font-semibold text-gray-700 flex items-center space-x-1">
                <Eye className="w-3 h-3" />
                <span>Correct Format Examples:</span>
              </p>
              <div className="space-y-1 text-xs text-gray-600">
                {channelType === 'instagram' && (
                  <>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">@yourhandle</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://instagram.com/yourhandle</code></p>
                  </>
                )}
                {channelType === 'tiktok' && (
                  <>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">@yourhandle</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://tiktok.com/@yourhandle</code></p>
                  </>
                )}
                {channelType === 'facebook' && (
                  <>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">YourPageName</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://facebook.com/yourpage</code></p>
                  </>
                )}
                {channelType === 'youtube' && (
                  <>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://youtube.com/c/yourchannel</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://youtube.com/channel/UCxxxxx</code></p>
                  </>
                )}
                {channelType === 'telegram' && (
                  <>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">@yourchannel</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://t.me/yourchannel</code></p>
                  </>
                )}
                {channelType === 'whatsapp' && (
                  <>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">0249158582</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">233249158582</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://wa.me/233249158582</code></p>
                  </>
                )}
                {channelType === 'website' && (
                  <>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://yourblog.com</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://www.yoursite.com</code></p>
                  </>
                )}
                {channelType === 'podcast' && (
                  <>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">Your Podcast Name</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://spotify.com/podcast/...</code></p>
                  </>
                )}
                {channelType === 'newsletter' && (
                  <>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">Your Newsletter Name</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">https://yournewsletter.com/subscribe</code></p>
                  </>
                )}
                {channelType === 'offline' && (
                  <>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">KNUST Campus Community</code></p>
                    <p>✓ <code className="px-1.5 py-0.5 bg-white rounded text-gray-800">Accra Mall Team</code></p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Multi-Store Selection - Only for Mobile App */}
        {channelType === 'mobile_app' && (
          <div>
            <label className="flex items-center mb-3 space-x-1 text-sm font-medium text-gray-700">
              <span>App Store Platforms</span>
              <span className="text-red-600">*</span>
            </label>
            <p className="mb-3 text-xs text-gray-500">
              Select all stores where your app is published. You can add links for each store below.
            </p>
            
            {/* Store Selection Buttons */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { value: 'play_store', label: 'Play Store', icon: Images.play_store },
                { value: 'apple_store', label: 'App Store', icon: Images.apple_store },
                { value: 'huawei', label: 'Huawei Store', icon: Images.huawei }
              ].map((store) => {
                const stores = channelData.stores || {};
                const isSelected = stores.hasOwnProperty(store.value);
                
                return (
                  <motion.button
                    key={store.value}
                    type="button"
                    onClick={() => {
                      const currentStores = channelData.stores || {};
                      if (isSelected) {
                        // Remove store if already selected
                        const newStores = { ...currentStores };
                        delete newStores[store.value];
                        handleInputChange('stores', newStores);
                      } else {
                        // Add store with empty link
                        handleInputChange('stores', {
                          ...currentStores,
                          [store.value]: ''
                        });
                      }
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-red-500 bg-red-50 shadow-md'
                        : 'border-gray-300 bg-white hover:border-gray-400'
                    }`}
                  >
                    <img 
                      src={store.icon} 
                      alt={store.label}
                      className="w-12 h-12 object-contain mb-2"
                    />
                    <span className={`text-xs font-medium ${
                      isSelected ? 'text-red-700' : 'text-gray-700'
                    }`}>
                      {store.label}
                    </span>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-2 right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <span className="text-white text-xs">✓</span>
                      </motion.div>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Store Link Inputs */}
            {channelData.stores && Object.keys(channelData.stores).length > 0 && (
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="mb-3 text-xs font-medium text-gray-700">
                  Enter store links for selected platforms:
                </p>
                {[
                  { value: 'play_store', label: 'Play Store', icon: Images.play_store },
                  { value: 'apple_store', label: 'App Store', icon: Images.apple_store },
                  { value: 'huawei', label: 'Huawei Store', icon: Images.huawei }
                ].map((store) => {
                  const stores = channelData.stores || {};
                  if (!stores.hasOwnProperty(store.value)) return null;
                  
                  return (
                    <div key={store.value} className="space-y-1">
                      <label className="flex items-center space-x-2 text-xs font-medium text-gray-700">
                        <img 
                          src={store.icon} 
                          alt={store.label}
                          className="w-4 h-4 object-contain"
                        />
                        <span>{store.label} Link</span>
                        <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={stores[store.value] || ''}
                        onChange={(e) => {
                          const newStores = {
                            ...(channelData.stores || {}),
                            [store.value]: e.target.value
                          };
                          handleInputChange('stores', newStores);
                        }}
                        className={`px-4 py-2.5 w-full text-sm placeholder-gray-500 text-gray-900 bg-white rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          errors?.stores?.[store.value] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={
                          store.value === 'play_store' 
                            ? 'https://play.google.com/store/apps/details?id=...'
                            : store.value === 'apple_store'
                            ? 'https://apps.apple.com/app/id...'
                            : 'https://appgallery.huawei.com/app/...'
                        }
                      />
                      {errors?.stores?.[store.value] && (
                        <p className="text-xs text-red-500">{errors.stores[store.value]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {errors?.stores && typeof errors.stores === 'string' && (
              <p className="mt-1 text-xs text-red-500">{errors.stores}</p>
            )}
            {errors?.store && (
              <p className="mt-1 text-xs text-red-500">{errors.store}</p>
            )}
          </div>
        )}

        {/* Audience Size */}
        <div>
          <label className="flex items-center mb-2 space-x-1 text-sm font-medium text-gray-700">
            <Users className="w-4 h-4" />
            <span>Estimated Audience Size</span>
            <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={channelData.audienceSize || ''}
            onChange={(e) => handleInputChange('audienceSize', e.target.value)}
            className={`px-4 py-3 w-full text-sm placeholder-gray-500 text-gray-900 bg-white rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
              errors?.audienceSize ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., 5,000 or 5K or 1.2M"
          />
          {errors?.audienceSize && (
            <p className="mt-1 text-xs text-red-500">{errors.audienceSize}</p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Number of followers, members, subscribers, or estimated reach
          </p>
        </div>

        {/* Primary Audience Location */}
        <div>
          <label className="flex items-center mb-2 space-x-1 text-sm font-medium text-gray-700">
            <MapPin className="w-4 h-4" />
            <span>Primary Audience Location</span>
            <span className="text-red-600">*</span>
          </label>
          <select
            value={channelData.location || ''}
            onChange={(e) => handleInputChange('location', e.target.value)}
            className={`px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
              errors?.location ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select primary location</option>
            {audienceLocations.map(location => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
          {errors?.location && (
            <p className="mt-1 text-xs text-red-500">{errors.location}</p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Where is your primary audience based?
          </p>
        </div>

        {/* Channel Purpose/Niche */}
        <div>
          <label className="flex items-center mb-2 space-x-1 text-sm font-medium text-gray-700">
            <Target className="w-4 h-4" />
            <span>Channel Purpose/Niche</span>
            <span className="text-red-600">*</span>
          </label>
          <select
            value={channelData.purpose || ''}
            onChange={(e) => handleInputChange('purpose', e.target.value)}
            className={`px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
              errors?.purpose ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select channel purpose</option>
            {channelPurposes.map(purpose => (
              <option key={purpose} value={purpose}>{purpose}</option>
            ))}
          </select>
          {errors?.purpose && (
            <p className="mt-1 text-xs text-red-500">{errors.purpose}</p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            What is the primary content category or niche?
          </p>
        </div>

        {/* Enhanced Verification Info Box */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-300 shadow-sm"
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="mb-2 text-sm font-semibold text-blue-900">
                ⚠️ Verification Process
              </p>
              <div className="space-y-1.5 text-xs text-blue-800">
                <div className="flex items-start space-x-2">
                  <span className="text-blue-600">•</span>
                  <span>Our team will <strong>visit and check</strong> each channel you provide</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-blue-600">•</span>
                  <span>Incorrect or inaccessible handles will <strong>delay your approval</strong></span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-blue-600">•</span>
                  <span>Double-check that handles match exactly as they appear on the platform</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-blue-600">•</span>
                  <span>Test your links before submitting to ensure they work correctly</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer Actions */}
      <div className="flex sticky bottom-0 gap-3 justify-end px-6 py-4 bg-gray-50 border-t border-gray-200">
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
        >
          Cancel
        </motion.button>
        <motion.button
          onClick={handleSave}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center px-6 py-2.5 space-x-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg transition-all duration-200 hover:from-red-600 hover:to-red-700 shadow-red-500/25"
        >
          <Save className="w-4 h-4" />
          <span>{editingIndex !== null ? 'Update' : 'Save'} Channel</span>
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ChannelFormDrawer;
